export const en = {
  header: {
    home: "Home",
    about: "About",
    mission: "Mission",
    program: "Levels",
    instructor: "Instructor",
    faq: "FAQ",
    gallery: "Gallery",
    contact: "Contact"
  },
  hero: {
    title: "Jeet Kune Do in Zurich",
    subtitle: "Develop practical self-defense skills, improve your fitness, and unlock your full potential.",
    bookTrial: "Book a Free Trial Class",
    viewSchedule: "View Class Schedule"
  },
  mission: {
    title: "Our Mission",
    intro: "We guide you from chaos to control — without turning you into someone you're not.",
    description: "J.K.D. Functional Defense was created with one purpose: to help normal people stay calm, clear, and in control when life gets chaotic.",
    focusTitle: "We focus on:",
    focusList: [
      "Staying out of panic",
      "Keeping order inside disorder",
      "Using your body with intention, not aggression",
      "Building inner and outer strength",
      "Growing step by step, with no pressure"
    ],
    cardTitle: "No violence. No ego. No unrealistic techniques.",
    cardText: "Only functionality, awareness and real safety."
  },
  about: {
    title: "What is Jeet Kune Do?",
    intro: "Jeet Kune Do, “The Way of the Intercepting Fist,” is the martial arts philosophy created by Bruce Lee in the 1960s. It focuses on practicality, adaptability, and efficiency—removing anything that doesn’t work in real situations. JKD isn’t a fixed style. It’s a flexible approach that evolves with you, encouraging you to absorb what is useful, discard what isn’t, and express your own strengths.",
    principlesTitle: "The Three Core Principles",
    principles: [
      { title: "Direct & Efficient", text: "Every movement has a purpose. JKD favors the most direct route to stop a threat, without unnecessary motions or flashy techniques." },
      { title: "Adaptive & Evolving", text: "No rigid forms, no kata. JKD adjusts to real-world scenarios and to the individual—your size, your mindset, your strengths." },
      { title: "Cross-Disciplinary", text: "JKD integrates effective elements from Wing Chun, boxing, wrestling, fencing, and other arts, forming a practical system built for real-life self-defense." }
    ]
  },
  program: {
    title: "JKD Progression Path",
    sections: {
      psychologicalgoals: "Psychological goals",
      technicalskills: "Technical skills",
      scenarios: "Scenarios",
      assessment: "Assessment",
      requirements: "Requirements"
    },
    levels: [
      {
        title: "Level 1 (Foundation)",
        color: "var(--white-gray)",
        slogan: "First you learn not to panic.",
        psychologicalgoals: [
          "Managing fear",
          "Controlling your breathing",
          "Awareness of space",
          "Instinctive protective posture"
        ],
        technicalskills: [
          "Basic footwork (advance, retreat, side step)",
          "Natural guard (non-martial, non-aggressive)",
          "Instinctive blocks, covers, “fence” position",
          "Understanding distance: safe – critical – danger zone"
        ],
        scenarios: [
          "Aggressive approach",
          "Argument that escalates",
          "Avoiding being pushed or trapped into a corner"
        ],
        assessment: [
          "Calm under pressure",
          "Correct posture",
          "Balance while moving",
          "Recognizing distance and timing"
        ]
      },
      {
        title: "Level 2 (Contact Management)",
        color: "#eeee64ff",
        slogan: "First you avoid, then you control. Only after that you react.",
        psychologicalgoals: [
          "Recognizing pre-aggressive signals",
          "Not getting dragged into the other person’s emotions"
        ],
        technicalskills: [
          "Hand-checking and contact reflexes",
          "Functional trapping (only what’s useful)",
          "Dynamic parries",
          "Lead hand + stop-hit concepts"
        ],
        scenarios: [
          "Push to the chest",
          "Grabbing the shirt/jacket",
          "Conflict at very close distance"
        ],
        assessment: [
          "Staying calm while being pushed",
          "Maintaining distance",
          "Simple, clean reactions"
        ]
      },
      {
        title: "Level 3 (Escape & Survival)",
        color: "#79df79ff",
        slogan: "First you free yourself, then you create distance.",
        psychologicalgoals: [
          "Controlled instinctive response",
          "Not freezing under physical contact"
        ],
        technicalskills: [
          "Releases from grabs",
          "Defense against chokes",
          "Escapes from holds and body locks",
          "Getting out of the clinch"
        ],
        scenarios: [
          "Aggression at zero distance",
          "Choke against a wall",
          "Grab from behind"
        ],
        assessment: [
          "Smooth releases without brute strength",
          "Constant movement",
          "Safe escape + creating distance"
        ]
      },
      {
        title: "Level 4 (Applied JKD)",
        color: "#2b52efff",
        slogan: "Simple. Direct. Inevitable.",
        technicalskills: [
          "Stop kick",
          "Intercepting fist",
          "Advanced footwork",
          "Distractions, fakes and angles",
          "Mobile trapping",
          "Strikes at close range"
        ],
        psychologicalgoals: [
          "Staying clear-headed while attacking",
          "Controlling your own aggression"
        ],
        scenarios: [
          "Attacker advancing quickly",
          "Wide swings / haymakers",
          "Sudden closing of the distance"
        ],
        assessment: [
          "Simple combinations",
          "Precise stop-hits",
          "Stable posture and balance"
        ]
      },
      {
        title: "Level 5 (Real Scenarios & Sparring)",
        color: "#000000",
        slogan: "It’s not sport. It’s controlling the chaos.",
        technicalskills: [
          "Theme-based sparring",
          "Sparring with changing distance",
          "Sparring under psychological pressure"
        ],
        scenarios: [
          "Tight spaces",
          "Room with obstacles",
          "Seated situations",
          "Concepts against two attackers"
        ],
        psychologicalgoals: [
          "Fast decision-making",
          "Managing adrenaline",
          "De-escalation under stress"
        ],
        assessment: [
          "Complete scenario",
          "Distance, timing, clarity",
          "Avoiding conflict when possible"
        ]
      },
      {
        title: "Level 6 (Instructor)",
        color: "#e63946",
        slogan: "Lead with ethics. Teach with calm.",
        requirements: [
          "Full command of all five previous levels",
          "Excellent communication",
          "Ability to maintain group safety",
          "Zero ego",
          "Discipline and ethics"
        ],
        technicalskills: [
          "Designing lessons",
          "Helping people who are afraid",
          "Correcting without humiliating",
          "Explaining psychology and legal aspects",
          "Risk management"
        ],
        assessment: [
          "Leading a full class",
          "Presenting a theory module",
          "Managing a scenario as an instructor",
          "Professional feedback evaluation"
        ]
      }
    ]
  },
  instructor: {
    title: "Your Instructor",
    name: "Michele Piccini",
    role: "Head Instructor",
    description1: "Michele Piccini is an instructor with many years of experience in martial arts and self-defense. His style is direct, human and practical.",
    description2: "His strengths lie in his ability to turn complex concepts into simple, natural and immediately usable movements. He knows how to make anyone feel comfortable, especially beginners, and he brings calm and control into challenging situations.",
    description3: "His background in the medical and academic fields gives him a unique sensitivity toward safety, communication, stress management and supporting people who experience fear or anxiety.",
    description4: "At JKD Functional we are not looking for fighters. We are looking for people who want to become more aware, more stable and safer.",
    description5: "Michele's method doesn’t try to change who you are. It helps you grow into the best and strongest version of yourself.",
    credentialsTitle: "Credentials",
    credentials: ["Certified JKD Instructor", "20+ Years Experience", "Self-Defense Specialist"],
    drawer: {
      bioTitle: "Biography",
      storyTitle: "My Story",
      storyText: "A journey of discovery through martial arts, learning from the best to teach the best.",
      timelineTitle: "Timeline",
      philosophyTitle: "Philosophy",
      philosophyText: "Adapt what is useful, reject what is useless, and add what is specifically your own."
    }
  },
  faq: {
    title: "Frequently Asked Questions",
    subtitle: "Everything you need to know before starting your JKD journey.",
    items: [
      {
        question: "What method do you teach at the JKD Functional Defense?",
        answer: {
          intro: "Our method is built on three core components:",
          list: [
            "Bruce Lee’s JKD principles – Practical, efficient techniques focused on real-world applicability.",
            "Modern urban self-defense – Scenario-based training, behavioral psychology, and clear decision-making.",
            "Stress management – Breathing, calmness, and mental control to improve your reactions under pressure."
          ],
          conclusion: [
            "We don’t just teach you how to fight.",
            "We teach you how to think, prevent, and respond—inside and outside the training space."
          ]
        }
      },
      {
        question: "Do I need prior martial arts experience?",
        answer: "Absolutely not! We welcome complete beginners. Many of our best students started with no experience. We'll teach you everything from the ground up, starting with basic stance, movement, and coordination before progressing to more complex techniques."
      },
      {
        question: "What equipment do I need for my first class?",
        answer: "For your trial class, just bring comfortable athletic clothing (t-shirt and training pants or shorts), indoor training shoes, and a water bottle. We provide gloves, focus mitts, and protective gear. Once you decide to continue, we recommend getting at least your own gloves, headgear and a mouthguard."
      },
      {
        question: "In what language are classes taught?",
        answer: "Classes are mainly taught in English. Michele is a native Italian speaker, and our group includes students who speak Italian, German, French and other languages. The mix of cultures creates a friendly, international environment where everyone feels welcome."
      },
      {
        question: "How do I book a free trial class?",
        answer: "Simple! Fill out <a href=\"https://docs.google.com/forms/d/1UYNWIYzQmMpG-_qwSAGJJ91QxA8dB4VTOehR0GbS8wQ/viewform \" target=\"_blank\">this Google Form</a>. You can also send a <a href=\"https://wa.me/41764002839\" target=\"_blank\">WhatsApp message</a>."
      },
      {
        question: "Do you offer private classes?",
        answer: "Yes, private classes are available. You can book individual training sessions directly with Michele."
      },
      {
        question: "Do you offer team building, workshops, or special events?",
        answer: "Yes, we organize workshops, team-building sessions, and custom events. Please fill out <a href=\"https://docs.google.com/forms/d/1Tcx-DYHsgSLb6orpUEuF_0ZKbHCKpx7S3Wypy7V3n-0/viewform \" target=\"_blank\">this Google form</a> to request a workshop, and <a href=\"https://docs.google.com/forms/d/1paT10uR_iVN1bUVqnP_lAECLVvv6yVBQHs3YAAMm5CU/viewform  \" target=\"_blank\">this one</a> to request a team-building event."
      },
      {
        question: "Can Michele teach at my company or another location?",
        answer: "Yes, Michele can travel to companies or external locations. Travel and on-site training are possible. Pricing is provided on request."
      },
      {
        question: "How much do classes cost?",
        answer: {
          list: [
            "Trial Class — Free",
            "1 Lesson — CHF 35 (valid 2 months)",
            "5-Class Pass — CHF 175 (valid 2 months)",
            "10-Class Pass — CHF 320 (valid 4 months)",
            "30-Class Pass — CHF 870 (valid 12 months)",
            "Private Lesson — price on request",
            "Team Building Events — price on request"
          ]
        }
      },
      {
        question: "Where is the school located?",
        answer: "The school is located at Kronenstrasse 12, 8006 Zürich, on the ground floor (floor 0). Sometimes we also train on the upper floor."
        }
    ],
    cta: {
      title: "Ready for Your Free Trial?",
      text: "Join us for a no-obligation trial class and discover the JKD difference.",
      button: "Book a Free Trial Class"
    }
  },
  instagram: {
    title: "Follow us on Instagram",
    button: "View Profile"
  },
  footer: {
    description: "JKD Functional Defense is a modern self-defense school based in Zurich. It’s a safe, welcoming environment designed for real people: students, workers, beginners, men and women of every age.",
    classesTitle: "Classes",
    days: {
      wednesday: "Wednesday",
      friday: "Friday"
    },
    privateLessons: "Private lessons are available at various times and locations.",
    copyright: "© 2025 Mike JKD Evolution. All rights reserved."
  }
};
